import React from "react";

import { Error, Info } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { UrlParamTypes } from "../../../../types/org-display";
import { useTrainingMigrationContext } from "./context/training-migration-provider";
import { HealCourseEnrolmentsFormState } from "./context/types";

// Mirrors the backend validator.regex on
// StartHealCourseEnrolmentsScriptRequest.course_ids.
export const COURSE_IDS_PATTERN = /^([a-z0-9]{24})(,[a-z0-9]{24})*$/;

// The form is valid to submit when processing all courses, or when the
// supplied course IDs match the backend's expected format.
export const isHealCourseEnrolmentsFormValid = (
  state: HealCourseEnrolmentsFormState,
): boolean =>
  state.processAllCourses || COURSE_IDS_PATTERN.test(state.courseIds.trim());

export const HealCourseEnrolmentsForm: React.FC = () => {
  const theme = useTheme();
  const { roleId } = useParams<UrlParamTypes>();
  const { healCourseEnrolmentsFormState, setHealCourseEnrolmentsFormState } =
    useTrainingMigrationContext();

  return (
    <Grid container paddingTop={2}>
      {/* Info Panel */}
      <Grid item xs={12} container direction="column">
        <Box
          bgcolor={theme.palette.info.light}
          borderRadius="5px"
          border={1}
          borderColor={theme.palette.info.main}
          padding={2}
          data-testid="heal-course-enrolments-info-panel"
        >
          <Grid container alignItems="flex-start" spacing={2}>
            <Grid item>
              <Info color="info" fontSize="large" />
            </Grid>
            <Grid item xs={10}>
              <Typography variant="body1">
                <strong>Start Heal Course Enrolments Script</strong>
              </Typography>
              <Typography variant="body2" component="div">
                <ul>
                  <li>
                    Compares state of course access with Training course
                    enrolment records
                  </li>
                  <li>Identifies discrepancies between the two sources</li>
                  <li>Fixes discrepancies to ensure data consistency</li>
                  <li>Writes findings to the hippo-hangfire logs</li>
                </ul>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* Organization ID */}
      <Grid container direction="row" alignItems="center" paddingTop={2}>
        <Grid item xs={5}>
          <Typography>Organisation ID</Typography>
        </Grid>
        <Grid item xs={7}>
          <TextField
            name="Organisation ID"
            inputProps={{
              "data-testid": "input-organisation-id",
            }}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            value={roleId}
          />
        </Grid>
      </Grid>

      {/* Dry Run Checkbox */}
      <Grid container direction="row" alignItems="center" paddingTop={1}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={healCourseEnrolmentsFormState.dryRun}
                onChange={(e) =>
                  setHealCourseEnrolmentsFormState({
                    ...healCourseEnrolmentsFormState,
                    dryRun: e.target.checked,
                  })
                }
                data-testid="input-dry-run"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Dry Run</Typography>
                <Typography variant="body2" color="textSecondary">
                  When enabled, only logs actions without making changes
                </Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>

      {/* Course Selection Mode */}
      <Grid container direction="row" alignItems="flex-start" paddingTop={2}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="body1">Course Selection</Typography>
            </FormLabel>
            <RadioGroup
              aria-label="course-selection"
              name="course-selection"
              value={
                healCourseEnrolmentsFormState.processAllCourses
                  ? "all"
                  : "specific"
              }
              onChange={(e) =>
                setHealCourseEnrolmentsFormState({
                  ...healCourseEnrolmentsFormState,
                  processAllCourses: e.target.value === "all",
                })
              }
            >
              <FormControlLabel
                value="all"
                control={<Radio data-testid="input-process-all-courses" />}
                label={
                  <Box>
                    <Typography variant="body2">Process All Courses</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Process enrolments for all courses in the organization
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="specific"
                control={<Radio data-testid="input-process-specific-courses" />}
                label={
                  <Box>
                    <Typography variant="body2">Specify Course IDs</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Process enrolments for specific courses only
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      {/* Course IDs Input - Only visible when specific courses selected */}
      {!healCourseEnrolmentsFormState.processAllCourses && (
        <Grid container direction="row" alignItems="center" paddingTop={2}>
          <Grid item xs={12}>
            <TextField
              name="Course IDs"
              inputProps={{
                "data-testid": "input-course-ids",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={healCourseEnrolmentsFormState.courseIds}
              onChange={(e) =>
                setHealCourseEnrolmentsFormState({
                  ...healCourseEnrolmentsFormState,
                  courseIds: e.target.value,
                })
              }
              placeholder="e.g., 507f1f77bcf86cd799439011,507f191e810c19729de860ea"
              error={
                !COURSE_IDS_PATTERN.test(
                  healCourseEnrolmentsFormState.courseIds.trim(),
                )
              }
              helperText={
                !COURSE_IDS_PATTERN.test(
                  healCourseEnrolmentsFormState.courseIds.trim(),
                )
                  ? "Enter one or more 24-character alphanumeric course IDs, comma-separated (no spaces)"
                  : "Comma-separated course IDs (24-character alphanumeric)"
              }
            />
          </Grid>
        </Grid>
      )}

      {/* Use Training Watch Service Checkbox */}
      <Grid container direction="row" alignItems="center" paddingTop={1}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={healCourseEnrolmentsFormState.useTrainingWatchService}
                onChange={(e) =>
                  setHealCourseEnrolmentsFormState({
                    ...healCourseEnrolmentsFormState,
                    useTrainingWatchService: e.target.checked,
                  })
                }
                data-testid="input-use-training-watch-service"
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Use Training Watch service
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  On by default. Reads each course&apos;s assignments directly
                  from srv-access-control-training-watch, which is much faster
                  for large organisations. Turn this off only in the rare case
                  where you suspect the training-watch data may be out of sync
                  with SpiceDB — that falls back to checking every training user
                  against every course via api-access-control.
                </Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>

      {/* Warning for non-dry-run mode */}
      {!healCourseEnrolmentsFormState.dryRun && (
        <Grid item xs={12} container direction="column" paddingTop={2}>
          <Box
            bgcolor={theme.palette.warning.main}
            borderRadius="5px"
            border={1}
            borderColor={theme.palette.warning.main}
            padding={2}
            data-testid="warning-panel"
          >
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Error fontSize="medium" />
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2">
                  <strong>Warning:</strong> Dry run is disabled. This script
                  will make actual changes to course enrolments.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
